import { useEffect, useState } from 'react'
import { memorizationApi } from '../lib/api'
import type { MemorizationData } from '../types'

interface UseDueVersesResult {
  verses: MemorizationData[]
  loading: boolean
}

const MAX = 5

/**
 * Returns up to 5 memorization verses whose nextReviewDate has passed,
 * sorted ascending by nextReviewDate (most overdue first). Errors are
 * swallowed and surfaced as an empty list — the dashboard hides the
 * "due for review" section when verses.length === 0, so an API blip
 * just results in the section being skipped this render.
 */
export function useDueVerses(): UseDueVersesResult {
  const [verses, setVerses] = useState<MemorizationData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    memorizationApi
      .listVerses()
      .then((all) => {
        if (cancelled) return
        const now = Date.now()
        const due = all
          .filter((v) => new Date(v.nextReviewDate).getTime() <= now)
          .sort(
            (a, b) =>
              new Date(a.nextReviewDate).getTime() -
              new Date(b.nextReviewDate).getTime(),
          )
          .slice(0, MAX)
        setVerses(due)
      })
      .catch(() => {
        if (cancelled) return
        setVerses([])
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { verses, loading }
}
